/**
 * Bug-255 — how a long-lived tab learns that a newer build is live.
 *
 * vite.config.ts gives every build one id (the commit SHA on Cloudflare Pages)
 * and ships it twice: inside this bundle as VITE_BUILD_ID, and as a tiny
 * `/version.json` next to index.html. A tab compares the two. The file must
 * never be cached — see public/_headers.
 *
 * The host answers an unknown path with its SPA fallback (200 text/html), so a
 * successful response proves nothing; only a JSON body carrying a buildId is
 * believed. Every other outcome — offline, 5xx, fallback HTML, a body from some
 * future format — is "cannot tell", which never prompts anyone.
 */

export const VERSION_FILE_PATH = "/version.json";

/** The id this bundle was built with, or null when it carries none (dev server, tests). */
export function getRunningBuildId(): string | null {
  const id = import.meta.env.VITE_BUILD_ID;
  return typeof id === "string" && id.trim() ? id.trim() : null;
}

/** The id of the build the host is serving right now, or null when that cannot be told. */
export async function fetchLatestBuildId(
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const response = await fetch(VERSION_FILE_PATH, {
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!response.ok) return null;
    if (!(response.headers.get("content-type") ?? "").includes("json")) {
      return null;
    }
    const body: unknown = await response.json();
    const buildId = (body as { buildId?: unknown } | null)?.buildId;
    return typeof buildId === "string" && buildId.trim() ? buildId.trim() : null;
  } catch {
    return null;
  }
}
